import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UserPlus, KeyRound, ShieldCheck, ShieldOff, Mail, Building2 } from "lucide-react";
import { L, SaveBtn, Toggle, Card, SectionTitle, genPassword, fmtDate } from "./shared";
import NewClientUserDialog from "./NewClientUserDialog";

export default function AccessTab({ pid, cfg, meta, reloadMeta, onSaved }) {
  const [acc, setAcc] = useState(null);
  const [companyId, setCompanyId] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);

  async function load() {
    try {
      const { data } = await api.get(`/staff/client-config/${pid}/access`);
      setAcc(data); setCompanyId(data.client_company_id || "");
      setSelected(new Set(data.users.filter(u => u.has_access).map(u => u.id)));
    } catch (e) { toast.error(apiErr(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  useEffect(() => {
    if (!companyId) { setCompanyUsers([]); return; }
    api.get(`/staff/client-users`, { params: { company_id: companyId } }).then(r => setCompanyUsers(r.data)).catch(() => setCompanyUsers([]));
  }, [companyId, acc]);

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/staff/client-config/${pid}/access`, { client_company_id: companyId || null, user_ids: [...selected], notify });
      setAcc(data); setSelected(new Set(data.users.filter(u => u.has_access).map(u => u.id)));
      const cfgRes = await api.get(`/staff/client-config/${pid}`); onSaved(cfgRes.data);
      toast.success("Acces actualizat");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  }

  async function resetPwd(u) {
    const pwd = genPassword();
    if (!window.confirm(`Resetezi parola pentru ${u.email}?\nParola temporară va fi: ${pwd}\n(La prima logare va fi obligat să o schimbe.)`)) return;
    try { await api.post(`/staff/client-users/${u.id}/reset-password`, { password: pwd }); navigator.clipboard?.writeText(pwd); toast.success("Parolă resetată (copiată în clipboard)"); }
    catch (e) { toast.error(apiErr(e)); }
  }

  async function toggleActive(u) {
    try { await api.put(`/staff/client-users/${u.id}`, { is_active: !u.is_active }); toast.success(u.is_active ? "Cont dezactivat" : "Cont reactivat"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  }

  if (!acc) return <div className="flex items-center gap-2 text-ink-muted"><Loader2 className="h-5 w-5 animate-spin" /> Se încarcă...</div>;

  // utilizatorii companiei selectate + cei care au deja acces (indiferent de companie)
  const byId = new Map();
  companyUsers.forEach(u => byId.set(u.id, u));
  acc.users.forEach(u => { if (u.has_access && !byId.has(u.id)) byId.set(u.id, { ...u, external: u.client_company_id !== companyId }); });
  const users = [...byId.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const company = (meta?.companies || []).find(c => c.id === companyId);

  return (
    <div className="space-y-6" data-testid="cc-access">
      <Card>
        <SectionTitle sub="Clientul (compania beneficiară) căreia îi aparține proiectul. Doar utilizatorii bifați mai jos văd acest proiect în portal.">Companie beneficiar</SectionTitle>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[320px] flex-1">
            <L label="Client / companie">
              <select value={companyId} onChange={e => { setCompanyId(e.target.value); }} data-testid="acc-company" className="inp">
                <option value="">— fără companie —</option>
                {(meta?.companies || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </L>
          </div>
          {company && (
            <div className="text-[12px] text-ink-muted flex items-center gap-2 pb-2"><Building2 className="h-4 w-4" /> {company.contact_name || "—"} · {company.contact_email || "—"}</div>
          )}
        </div>
        <p className="text-[11px] text-ink-muted mt-2">Companiile și utilizatorii se administrează din „Clienți & utilizatori" (butonul din antet).</p>
      </Card>

      <Card>
        <div className="flex items-start justify-between gap-3 mb-3">
          <SectionTitle sub="Bifează utilizatorii-client care au voie să vadă acest proiect. Restricția se aplică în API, DB și la acces direct prin URL.">Utilizatori cu acces la acest proiect</SectionTitle>
          <button onClick={() => setShowNew(true)} data-testid="acc-new-user"
            className="inline-flex items-center gap-2 px-3 py-2 bg-ink text-white text-[13px] rounded-lg hover:bg-brand whitespace-nowrap">
            <UserPlus className="h-4 w-4" /> Utilizator client nou
          </button>
        </div>
        {users.length === 0 ? (
          <div className="py-10 text-center text-ink-muted text-sm border border-dashed border-neutral-200 rounded-lg">
            {companyId ? "Compania nu are utilizatori. Creează primul utilizator-client." : "Alege o companie sau creează un utilizator nou."}
          </div>
        ) : (
          <div className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg overflow-hidden">
            {users.map(u => (
              <div key={u.id} className={`flex items-center gap-3 px-4 py-3 ${u.is_active === false ? "opacity-60" : ""}`} data-testid={`acc-user-${u.email}`}>
                <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} data-testid={`acc-check-${u.email}`} className="h-4 w-4 accent-[#111]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink flex items-center gap-2 flex-wrap">
                    {u.name}
                    {u.is_active === false && <span className="text-[10px] font-mono uppercase bg-red-50 text-red-600 px-1.5 rounded">dezactivat</span>}
                    {u.must_change_password && <span className="text-[10px] font-mono uppercase bg-amber-50 text-amber-700 px-1.5 rounded">onboarding în așteptare</span>}
                    {u.external && <span className="text-[10px] font-mono uppercase bg-neutral-100 text-ink-muted px-1.5 rounded">altă companie</span>}
                  </div>
                  <div className="text-[12px] text-ink-muted flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</span>
                    {u.job_title && <span>{u.job_title}</span>}
                    <span>Ultima logare: {u.last_login ? fmtDate(u.last_login) : "niciodată"}</span>
                  </div>
                </div>
                <button onClick={() => resetPwd(u)} title="Resetează parola" className="p-2 text-ink-muted hover:text-ink" data-testid={`acc-reset-${u.email}`}><KeyRound className="h-4 w-4" /></button>
                <button onClick={() => toggleActive(u)} title={u.is_active === false ? "Reactivează" : "Dezactivează"} className="p-2 text-ink-muted hover:text-red-600" data-testid={`acc-toggle-${u.email}`}>
                  {u.is_active === false ? <ShieldCheck className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 mt-4">
          <SaveBtn onClick={save} saving={saving} label="Salvează accesul" testid="acc-save" />
          <Toggle checked={notify} onChange={setNotify} label="Notifică utilizatorii nou adăugați (notificare + email)" testid="acc-notify" />
          <span className="text-[12px] text-ink-muted ml-auto">{selected.size} utilizator(i) cu acces</span>
        </div>
      </Card>

      {showNew && (
        <NewClientUserDialog
          companies={meta?.companies || []}
          defaultCompanyId={companyId}
          defaultProjectIds={[pid]}
          projects={[{ id: pid, name: cfg.project?.name, code: cfg.project?.code }]}
          onClose={() => setShowNew(false)}
          onCreated={async () => { await reloadMeta(); await load(); }}
        />
      )}
    </div>
  );
}
